import * as displayPlugin from "../../lib/DisplayPlugin/DisplayPlugin";
import * as errors from "../../lib/errors";
import * as posActions from "../../lib/posActions";
import * as posMenuEndpoints from "../../lib/api/menus";
import * as posSelectors from "../../lib/posSelectors";
import * as posSettings from "../../lib/api/settings";
import * as posStoreEndpoints from "../../lib/api/stores";
import * as routeHelpers from "../../lib/routeHelpers";
import * as Sentry from "@sentry/capacitor";
import CashierCheckout from "../CashierCheckout";
import CashPull from "../CashPull";
import describeNetworkError from "../../lib/describeNetworkError";
import ErrorModal from "../ErrorModal";
import instance from "../../lib/api/instance";
import logger from "../../lib/sentryLogger";
import React, {useEffect, useCallback, useContext, useState} from "react";
import Spinner from "../Spinner";
import styles from "./App.module.css";
import useAppActivityManager from "../../lib/useAppActivityManager";
import usePrevious from "../../lib/usePrevious";
import useRefreshPendingOrders from "../../lib/useRefreshPendingOrders";
import {API, Actions, Constants} from "habit-core";
import {AppSettingsContext} from "../AppSettingsContext";
import {Device} from "@capacitor/device";
import {Outlet, useLocation, useNavigate} from "react-router-dom";
import {StationContext} from "../StationContext";
import {useAppDispatch, useAppSelector} from "../../lib/hooks";
import {RefreshContext} from "../RefreshContext";

const REQUIRED_DPI = 160;

const strings = {
    genericErrorTitle: "Whoops!",
    genericErrorMessage: "Something went wrong. Please try again.",
    retry: "Retry",
    discountRemoved:
        "The applied discount(s) could not be validated and have been removed.",
    discountRemovedCartEmpty:
        "The previously applied discount(s) have been removed because the order is now empty.",
    discountErrorModalTitle: "Discount(s) Automatically Removed",
    dpiErrorModalTitle: "Display Configuration Warning",
    dpiDetectedWarningMessage: (detectedDpi: number) =>
        `This device's display configuration is incorrect and may result in some visual issues. The POS can still be used, but please contact IT. \n Issue: DPI is set to ${detectedDpi}. It should be set to ${REQUIRED_DPI}.`,
    dpiUndetectedWarningMessage:
        "This device's display configuration could not be read & may have some issues. The POS can still be used but please contact IT.",
    dismiss: "Dismiss",
};

// TODO: #188 - Think about how we want to load this onto pos config
const requiresDriveThruDetails = true;

export default function App() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState<boolean>(true);
    const [errorMessage, setErrorMessage] = useState("");
    const [dpiWarningMessage, setDpiWarningMessage] = useState("");
    const dispatch = useAppDispatch();

    const currentOrder = useAppSelector((state) => state.currentOrder);
    const posCurrentOrder = useAppSelector((state) => state.pos.currentOrder);
    const subtotalCents = useAppSelector((state) =>
        posSelectors.getCurrentOrderSubtotalSansVoidedCents(state),
    );
    const stationMode = useAppSelector((state) => state.pos.station.mode);
    const deviceId = useAppSelector((state) => state.pos.deviceId);
    const currentOrderStoreId = useAppSelector(
        (state) => state.currentOrder.storeId,
    );

    useEffect(() => {
        instance.defaults.headers.common["X-POS-Station-Mode"] = stationMode;
        if (deviceId) {
            instance.defaults.headers.common["X-POS-Device-Id"] = deviceId;
        }
    }, [stationMode, deviceId]);

    const {
        globalMenuLastUpdateDateRef,
        storeMenuLastUpdateDateRef,
        storeSettingsLastUpdateDateRef,
    } = useContext(RefreshContext);

    const {
        isTraining,
        resetCurrentOrderStateOnCartEmptied,
        setResetCurrentOrderStateOnCartEmptied,
    } = useContext(StationContext);
    const {setIsParallel, setIsLab} = useContext(AppSettingsContext);
    const prevIsTraining = usePrevious(isTraining);

    const [isAfterInitialLoad, setIsAfterInitialLoad] = useState(false);
    const initialLoad = () => {
        setLoading(true);
        Promise.all([
            Device.getId(),
            posSettings.get(),
            posStoreEndpoints.getStoreSettings(),
            displayPlugin.getDPI(),
        ])
            .then(([deviceIdResp, settings, storeSettings, dpi]) => {
                // configure sentry tags + set logger store number so it is included with every log sent to sentry.
                logger.setStoreNumber(storeSettings.storeNumber);
                Sentry.setTag("storeNumber", storeSettings.storeNumber);
                Sentry.setTag("stationMode", settings.stationMode);
                Sentry.setTag("deviceId", deviceIdResp.identifier);

                if (dpi === undefined) {
                    logger.warn("Unable to detect the device DPI");
                    setDpiWarningMessage(strings.dpiUndetectedWarningMessage);
                } else if (dpi !== REQUIRED_DPI) {
                    setDpiWarningMessage(
                        strings.dpiDetectedWarningMessage(dpi),
                    );
                    logger.warn(
                        `The device has a dpi of ${dpi} while we require ${REQUIRED_DPI}.`,
                    );
                }

                // backend keeps IP -> station mode mapping. on startup, set mode to what we receive in settings.
                const stationMode = settings.stationMode;
                const laneAssignment =
                    stationMode === "drive_thru_order_taker"
                        ? settings.laneAssignment
                        : null;

                let initialOrderType: API.models.OrderType = "in_store";
                if (
                    stationMode === "drive_thru_order_taker" ||
                    stationMode === "drive_thru_order_fulfillment" ||
                    stationMode === "line_buster"
                ) {
                    initialOrderType = "drive_thru";
                }

                const storeId = storeSettings.storeId;
                // set the device id
                dispatch(posActions.setDeviceId(deviceIdResp.identifier));

                setIsParallel(storeSettings.isParallelMode);
                setIsLab(storeSettings.isLab);

                // initialize the station
                dispatch(
                    posActions.initializeStation(
                        stationMode,
                        requiresDriveThruDetails,
                        laneAssignment,
                    ),
                );
                dispatch(
                    posActions.setSupportedExternalTenders(
                        storeSettings.externalTenders ?? [],
                    ),
                );

                // initialize charity round up settings
                const charitySettings = storeSettings.charitySettings;
                dispatch(
                    posActions.initializeCharityRoundUpSettingsImmediate(
                        charitySettings.isEnabled,
                        charitySettings.mode,
                        charitySettings.charityName,
                        charitySettings.prompt,
                        charitySettings.rewardMinimumCents,
                        charitySettings.rewardMessage,
                    ),
                );

                // initialize store address and tax rate.
                const storeInfo = {
                    id: storeId,
                    taxRate: storeSettings.taxRate,
                    address: storeSettings.address,
                    established: "n/a",
                    name: "n/a",
                    status: Constants.storeStatus.ONLINE,
                    storeNumber: 0,
                    maxOrderCost: 500,
                    maxOrderCostCents: 50000,
                    supportsCurbside: false,
                    supportsCharlane: false,
                    curbsideAutoCheckIn: {enabled: false},
                    supportsDineIn: true,
                    hours: {
                        driveThru: [],
                        inStore: [],
                        modified: {
                            driveThru: [],
                            inStore: [],
                        },
                    },
                    images: [],
                    supportsQrToGo: false,
                    supportsQrDineIn: false,
                    isItemComboUpsellEnabled: false,
                    supportsPickupLane: false,
                    maxGiftCardCashOutCents:
                        storeSettings.maxGiftCardCashOutCents,
                    isCondimentUpsellEnabled: false,
                    fundraiserSupportEnabled: false,
                };
                dispatch(Actions.storeActions.setStores([storeInfo]));

                return Promise.all([
                    stationMode,
                    posMenuEndpoints.getGlobalMenuLastUpdateDate(),
                    posMenuEndpoints.getStoreMenuLastUpdateDate(storeId),
                    posStoreEndpoints.getStoreSettingsLastUpdateDate(),
                    dispatch(Actions.menuActions.loadGlobalMenu()),
                    dispatch(
                        Actions.currentOrderActions.selectStore(
                            storeId,
                            initialOrderType,
                            false,
                        ),
                    ),
                    dispatch(posActions.initializePosAllPreps(storeId)),
                    dispatch(posActions.initializePosAddOns()),
                ]);
            })
            .then(
                ([
                    stationMode,
                    globalMenuLastUpdateDate,
                    storeMenuLastUpdateDate,
                    storeSettingsLastUpdateDate,
                ]) => {
                    globalMenuLastUpdateDateRef.current =
                        globalMenuLastUpdateDate;
                    storeMenuLastUpdateDateRef.current =
                        storeMenuLastUpdateDate;
                    storeSettingsLastUpdateDateRef.current =
                        storeSettingsLastUpdateDate;
                    return Promise.all([stationMode]);
                },
            )
            .then(([stationMode]) => {
                const reserverOrderPromise =
                    stationMode !== "drive_thru_order_fulfillment"
                        ? dispatch(posActions.reserveCurrentOrderId(isTraining))
                        : Promise.resolve();

                return Promise.all([stationMode, reserverOrderPromise]);
            })
            .then(([stationMode]) => {
                setLoading(false);
                navigate(routeHelpers.deviceSetup(), {
                    state: {
                        skipCCRegistrationCheck:
                            stationMode === "drive_thru_order_taker" ||
                            stationMode === "line_buster",
                    },
                });
                setIsAfterInitialLoad(true);
            })
            .catch((err) => {
                const errMsg = describeNetworkError(err).join("\n");
                logger.error("App initial load failed.", {
                    errorMessage: errMsg,
                });
                setErrorMessage(errMsg);
            });
    };

    /* fire once on app startup */
    useEffect(() => {
        initialLoad();
    }, []);

    const location = useLocation();
    // we don't want to poll on checkout step 0 -> step 2
    const disablePollingOnCheckout =
        location.pathname.includes("checkout") &&
        !location.pathname.includes(routeHelpers.otherOrders()) &&
        !location.pathname.includes(routeHelpers.orderComplete());

    const isManagerCancelScreen = location.pathname.includes(
        routeHelpers.cancelOrder(),
    );
    const getPendingOrdersEnabled =
        isAfterInitialLoad &&
        (stationMode === "drive_thru_order_fulfillment" ||
            stationMode === "front_of_house_1" ||
            stationMode === "front_of_house_2") &&
        !disablePollingOnCheckout &&
        !isManagerCancelScreen;

    /* hooks to fetch pending orders */
    useRefreshPendingOrders(
        getPendingOrdersEnabled,
        stationMode === "drive_thru_order_fulfillment" ? "drive_thru" : "kiosk",
        isTraining,
    );

    const reserveOrderId = useCallback((isTraining: boolean) => {
        setLoading(true);
        dispatch(posActions.reserveCurrentOrderId(isTraining))
            .then(() => {
                setLoading(false);
            })
            .catch((err) => {
                const errMsg = describeNetworkError(err).join("\n");
                logger.error("Failed to reserve new order id.", {
                    errorMessage: errMsg,
                });
                setErrorMessage(errMsg);
            });
    }, []);

    /* should reserve an order id if isTraining changes and we are not the drive_thru_order_fulfillment role */
    useEffect(() => {
        if (
            isTraining !== prevIsTraining &&
            stationMode !== "drive_thru_order_fulfillment" &&
            isAfterInitialLoad
        ) {
            reserveOrderId(isTraining);
        }
    }, [
        isTraining,
        prevIsTraining,
        isAfterInitialLoad,
        stationMode,
        reserveOrderId,
    ]);

    const inactiveCallback = useCallback(() => {
        return Promise.all([
            posMenuEndpoints.getGlobalMenuLastUpdateDate(),
            posMenuEndpoints.getStoreMenuLastUpdateDate(
                currentOrderStoreId ?? "",
            ),
            posStoreEndpoints.getStoreSettingsLastUpdateDate(),
        ])
            .then(
                async ([
                    _globalMenuLastUpdateDate,
                    _storeMenuLastUpdateDate,
                    _storeSettingsLastUpdateDate,
                ]) => {
                    if (
                        globalMenuLastUpdateDateRef.current === null ||
                        globalMenuLastUpdateDateRef.current <
                            _globalMenuLastUpdateDate
                    ) {
                        await dispatch(Actions.menuActions.loadGlobalMenu());
                        await dispatch(posActions.initializePosAddOns());
                        globalMenuLastUpdateDateRef.current =
                            _globalMenuLastUpdateDate;
                    }
                    if (
                        storeMenuLastUpdateDateRef.current === null ||
                        storeMenuLastUpdateDateRef.current <
                            _storeMenuLastUpdateDate
                    ) {
                        await dispatch(
                            posActions.refreshStoreMenu(
                                currentOrderStoreId ?? "",
                                currentOrder.orderType ?? "in_store", // this shouldn't be null
                            ),
                        );
                        await dispatch(
                            posActions.initializePosAllPreps(
                                currentOrderStoreId ?? "",
                            ),
                        );
                        storeMenuLastUpdateDateRef.current =
                            _storeMenuLastUpdateDate;
                    }
                    if (
                        storeSettingsLastUpdateDateRef.current === null ||
                        storeSettingsLastUpdateDateRef.current <
                            _storeSettingsLastUpdateDate
                    ) {
                        const storeSettings =
                            await posStoreEndpoints.getStoreSettings();
                        const charitySettings = storeSettings.charitySettings;
                        await dispatch(
                            posActions.initializeCharityRoundUpSettingsImmediate(
                                charitySettings.isEnabled,
                                charitySettings.mode,
                                charitySettings.charityName,
                                charitySettings.prompt,
                                charitySettings.rewardMinimumCents,
                                charitySettings.rewardMessage,
                            ),
                        );
                        await dispatch(
                            posActions.setSupportedExternalTenders(
                                storeSettings.externalTenders ?? [],
                            ),
                        );

                        // initialize store address and tax rate.
                        const storeInfo = {
                            id: storeSettings.storeId,
                            taxRate: storeSettings.taxRate,
                            address: storeSettings.address,
                            established: "n/a",
                            name: "n/a",
                            status: Constants.storeStatus.ONLINE,
                            storeNumber: storeSettings.storeNumber,
                            maxOrderCost: 500,
                            maxOrderCostCents: 50000,
                            supportsCurbside: false,
                            supportsCharlane: false,
                            curbsideAutoCheckIn: {enabled: false},
                            supportsDineIn: true,
                            hours: {
                                driveThru: [],
                                inStore: [],
                                modified: {
                                    driveThru: [],
                                    inStore: [],
                                },
                            },
                            images: [],
                            supportsQrToGo: false,
                            supportsQrDineIn: false,
                            isItemComboUpsellEnabled: false,
                            supportsPickupLane: false,
                            maxGiftCardCashOutCents:
                                storeSettings.maxGiftCardCashOutCents,
                            isCondimentUpsellEnabled: false,
                            fundraiserSupportEnabled: false,
                        };
                        await dispatch(
                            Actions.storeActions.setStores([storeInfo]),
                        );
                        storeSettingsLastUpdateDateRef.current =
                            _storeSettingsLastUpdateDate;
                    }
                },
            )
            .catch((err) => {
                const errMsg = describeNetworkError(err).join("\n");
                logger.error(
                    "Failed to refresh menu data and store settings while inactive.",
                    {
                        errorMessage: errMsg,
                    },
                );
            });
    }, [currentOrderStoreId, currentOrder.orderType]);

    useAppActivityManager(inactiveCallback);

    useEffect(() => {
        const hasItemsAndOrCombos =
            currentOrder.itemCustomizationIds.length ||
            currentOrder.comboCustomizationIds.length;
        const hasGiftCards =
            posCurrentOrder.giftCards.addFunds.length ||
            posCurrentOrder.giftCards.purchase.length;

        if (
            !posCurrentOrder.startTime &&
            (hasItemsAndOrCombos || hasGiftCards)
        ) {
            // if order hasn't started yet and user just added an item/combo
            dispatch(posActions.setCurrentOrderStartTime(new Date()));
        } else if (
            !hasItemsAndOrCombos &&
            !hasGiftCards &&
            resetCurrentOrderStateOnCartEmptied
        ) {
            setResetCurrentOrderStateOnCartEmptied(false);
            dispatch(posActions.clearCurrentOrder(currentOrderStoreId ?? ""));
            reserveOrderId(isTraining);
        } else if (
            posCurrentOrder.startTime &&
            !hasItemsAndOrCombos &&
            !hasGiftCards
        ) {
            // reset start time and allergy warning if remove only item/combo in order
            dispatch(posActions.setCurrentOrderStartTime(null));
            dispatch(posActions.setAllergyWarningShown(false));
        }
    }, [
        posCurrentOrder.startTime,
        currentOrder.itemCustomizationIds,
        currentOrder.comboCustomizationIds,
        posCurrentOrder.giftCards,
        resetCurrentOrderStateOnCartEmptied,
        isTraining,
        reserveOrderId,
    ]);

    /* attempt to auto combo when the item customization ids in the current order changes */
    useEffect(() => {
        if (currentOrder.itemCustomizationIds.length >= 2) {
            dispatch(Actions.currentOrderActions.autoComboOrderItems());
        }
    }, [currentOrder.itemCustomizationIds]);

    const [discountErrorMessage, setDiscountErrorMessage] = useState("");
    // revalidate discounts when subtotal changes
    useEffect(() => {
        posCurrentOrder.discounts.forEach((d) => {
            dispatch(
                posActions.currentOrderValidateDiscount(
                    d.discountCode,
                    d.rewardId,
                    d.employeeId,
                    undefined,
                    true,
                ),
            )
                .then((result) => {
                    dispatch(
                        posActions.currentOrderRemoveDiscount(
                            d.discountCode,
                            d.rewardId,
                        ),
                    );
                    dispatch(
                        posActions.currentOrderAddDiscount(
                            d.discountCode,
                            d.rewardId,
                            result.redemptionAmountCents,
                            result.redemptionAmountPercentage,
                            result.discountType,
                        ),
                    );
                })
                .catch((err) => {
                    if (err.message === errors.NO_DISCOUNTS_CART_EMPTY) {
                        setDiscountErrorMessage(
                            strings.discountRemovedCartEmpty,
                        );
                    } else {
                        setDiscountErrorMessage(strings.discountRemoved);
                    }
                    dispatch(
                        posActions.currentOrderRemoveDiscount(
                            d.discountCode,
                            d.rewardId,
                        ),
                    );
                });
        });
    }, [subtotalCents]);

    const onRetry = () => {
        setErrorMessage("");
        // if not true, initial load failed at some step, retry
        if (!isAfterInitialLoad) {
            initialLoad();
        } else if (stationMode !== "drive_thru_order_fulfillment") {
            reserveOrderId(isTraining); // only other way for the error message to be set.
        }
    };

    if (errorMessage) {
        return (
            <div>
                <ErrorModal
                    onClose={onRetry}
                    errorMessage={errorMessage}
                    closeButtonLabel={strings.retry}
                    title={strings.genericErrorTitle}
                />
            </div>
        );
    }

    if (dpiWarningMessage) {
        return (
            <div>
                <ErrorModal
                    onClose={() => setDpiWarningMessage("")}
                    errorMessage={dpiWarningMessage}
                    closeButtonLabel={strings.dismiss}
                    title={strings.dpiErrorModalTitle}
                    showITInfo
                />
            </div>
        );
    }

    if (loading) {
        return (
            <div className={styles.loading}>
                <Spinner />
            </div>
        );
    }

    return (
        <div className={styles.app}>
            <CashierCheckout className={styles.cashierCheckout} />
            <Outlet />
            <div className={styles.gitVersion}>
                {import.meta.env.REACT_APP_GIT_REVISION}
                {import.meta.env.DEV ? "-dev" : null}
            </div>
            {discountErrorMessage ? (
                <ErrorModal
                    onClose={() => setDiscountErrorMessage("")}
                    errorMessage={discountErrorMessage}
                    title={strings.discountErrorModalTitle}
                    backgroundColor="grey"
                />
            ) : null}
            <CashPull />
        </div>
    );
}
